import React, {
  SetStateAction,
  useState,
  Dispatch,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import _ from 'lodash'

import Accordion, { AccordionItemElement } from './accordion'
import BurgerIcon from './burger-icon'
import Button, { DeleteButton, NavigateButton } from './button'
import ButtonDropdown, {
  DropdownButtonItem,
  DropdownLabel,
} from './button-dropdown'
import CampaignCodeAdministrationAddNew from './campaign-codes-administration-add-new'
import { BoxedText, Heading } from './typography'
import Input, { ClickEditInput, Label } from './input'
import Link from './link'
import Modal from './modal'
import Row from './row'
import SelectBox, { SelectBoxChecklist, SelectBoxSimple } from './select-box'
import Tooltip from './tooltip'
import { currentUserDetails, linkOrCode } from '../api/apollo/variables'
import { getCampaignCodeGenerator } from '../api/graphql/track-create-client'
import {
  addNewGeneratorParameter,
  deleteGeneratorParameterByID,
  updateGeneratorParamAddMultipleSelectOptions,
  updateGeneratorParameterByID,
  updateGeneratorParameterOrder,
} from '../api/graphql/track-edit-client'
import { getUserAccounts } from '../api/graphql/user-client'
import {
  DateFormats,
  InputTypes,
  ValidationChecks,
  ParamColors,
  ParamDefs,
} from '../api/types'
import LockedIcon from '../assets/svgs/padlock-locked'
import {
  ga4Parameters,
  popularParameters,
  TemplateParam,
} from '../core/track-parameters'
import { dateOptions, dynamicTextValues, supportEmail } from '../core/constants'
import { isAdminUser, prepareInput } from '../helpers'
import {
  checkCircularDependency,
  getCopyFromValues,
  parentOptionsAreEqual,
} from '../helpers/track-module'
import useLogAction from '../hooks/useLogAction'
import useMobile from '../hooks/useMobile'
import useOnboarding from '../hooks/useOnboarding'
import styles from '../styles/track-edit-params-drag-and-drop.module.scss'
import { GetCampaignCodeGeneratorQuery } from '../__gql-types__/graphql'

interface OnlyShowIfProps {
  fieldID: string
  parameterDependsOn?: {
    parentFieldID: string
    parentOptionIDs: string[]
  } | null
  dropdownParams: ParamDefs[]
  onlyShowIf: {
    parentField: string
    parentValues: string[]
  } | null
  setOnlyShowIf: (
    newValue: {
      parentField: string
      parentValues: string[]
    } | null,
  ) => void
  parentFieldOptions: {
    fieldID: string
    fieldName: string
  }[]
  isLocked: boolean
  setShowUnlockModal: () => void
  trackAction: (
    action: string,
    id: string,
    newVal: string,
    oldVal: string,
  ) => void
  updateParamByID: any
}

const OnlyShowIf = ({
  fieldID,
  parameterDependsOn,
  dropdownParams,
  onlyShowIf,
  setOnlyShowIf,
  parentFieldOptions,
  isLocked,
  setShowUnlockModal,
  trackAction,
  updateParamByID,
}: OnlyShowIfProps) => {
  const { parentField, parentValues } = onlyShowIf || {}

  const linkCopy = useReactiveVar(linkOrCode)

  const isMobile = useMobile(1200)

  const { companyID } = useReactiveVar(currentUserDetails)

  // TODO: Get all workspaces for a company
  const { data: userAccountData } = useQuery(getUserAccounts)

  const userAdminWorkspaces = useMemo(() => {
    if (!companyID || !userAccountData) return []

    return userAccountData.currentUser.userAccountProfiles
      .filter(
        (account) =>
          account.companyID === companyID &&
          isAdminUser(account.userPermission),
      )
      .map((account) => ({
        optionID: account.accountID,
        optionName: account.accountName,
      }))
  }, [userAccountData, companyID])

  const parentValueOptions = useMemo(() => {
    if (!parentField) return []

    if (parentField === 'account') {
      return userAdminWorkspaces
    }

    const parentFieldFull = dropdownParams.find(
      (param) => param.fieldID === parentField,
    )

    if (!parentFieldFull) return []

    return parentFieldFull.selectFields || []
  }, [dropdownParams, parentField, userAdminWorkspaces])

  return (
    <>
      <Label id={`${fieldID}-onlyShowIf`} className={styles.parameterRuleLabel}>
        <Tooltip
          id={`${fieldID}-only-show-if-tooltip`}
          useIcon
          maxWidth={300}
          clickable
          tooltipPosition={isMobile ? 'bottom' : 'right'}
          tooltipMessage={
            <span>
              This parameter will only appear in{' '}
              <BoxedText>
                <Link href="/track/create-links">
                  {dynamicTextValues.trackCreatePage[linkCopy]}
                </Link>
              </BoxedText>{' '}
              if the following dropdown value(s) are selected. Example: If
              Medium=PPC, show utm_term parameter.
            </span>
          }
        >
          Only show if
        </Tooltip>
      </Label>
      <SelectBox
        id={`${fieldID}-onlyShowIf`}
        className={styles.parameterInput}
        placeholder="Select parameter"
        labelKey="fieldName"
        valueKey="fieldID"
        value={
          onlyShowIf
            ? parentFieldOptions.find(
                (option) => option.fieldID === parentField,
              )
            : null
        }
        options={parentFieldOptions}
        onChange={(newValue) => {
          if (isLocked) {
            setShowUnlockModal()

            return
          }

          setOnlyShowIf(
            newValue?.fieldID
              ? { parentField: newValue?.fieldID, parentValues: [] }
              : null,
          )
        }}
      />
      {!!parentField && (
        <>
          <Label
            id={`${fieldID}-onlyShowIfValues`}
            className={classNames(
              styles.parameterRuleLabel,
              styles.inlineLabel,
            )}
          >
            is
          </Label>
          <SelectBoxChecklist
            id={`${fieldID}-onlyShowIfValues`}
            className={classNames(styles.parameterInput, styles.inlineDropdown)}
            labelKey="optionName"
            valueKey="optionID"
            value={parentValueOptions.filter(
              (option) =>
                parentValues && parentValues.indexOf(option.optionID) > -1,
            )}
            options={parentValueOptions}
            onChange={(newValue) => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              setOnlyShowIf({
                parentField,
                parentValues: newValue.map(({ optionID }) => optionID),
              })
            }}
          />
          <Button
            variant="secondary"
            isDisabled={
              !parentValues ||
              parentValues.length === 0 ||
              (!!parameterDependsOn &&
                parentOptionsAreEqual(
                  parentValues,
                  parameterDependsOn.parentOptionIDs,
                ))
            }
            onPress={() => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              updateParamByID({
                variables: {
                  updateFieldID: fieldID,
                  parameterDependsOn: {
                    parentFieldID: parentField,
                    parentOptionIDs: parentValues,
                  },
                },
              })

              trackAction(
                'update-generator-parameter-depends-on-field',
                fieldID,
                JSON.stringify({
                  parentFieldID: parentField,
                  parentOptionIDs: parentValues,
                }),
                JSON.stringify(parameterDependsOn),
              )
            }}
          >
            Update
          </Button>
          {parameterDependsOn && parameterDependsOn.parentOptionIDs.length > 0 && (
            <Button
              variant="text"
              isDisabled={
                !parameterDependsOn ||
                parameterDependsOn.parentOptionIDs.length === 0
              }
              onPress={() => {
                if (isLocked) {
                  setShowUnlockModal()

                  return
                }

                setOnlyShowIf(null)

                updateParamByID({
                  variables: {
                    updateFieldID: fieldID,
                    parameterDependsOn: null,
                  },
                })

                trackAction(
                  'update-generator-parameter-depends-on-field',
                  fieldID,
                  'null',
                  JSON.stringify(parameterDependsOn),
                )
              }}
            >
              Clear
            </Button>
          )}
        </>
      )}
    </>
  )
}

const inputTypesDropdown = [
  {
    name: 'Dropdown',
    value: 'select',
    tooltip:
      'Users select a value from a list of options. Dropdown options are maintained by your admins on the **Track > Edit dropdowns** page.',
  },
  {
    name: 'Free text',
    value: 'input',
    tooltip:
      'Users can type or paste values, with data validation rules selected by your admins on the **Track > Edit parameters** page.',
  },
  {
    name: 'Date',
    value: 'date',
    tooltip: `Users can select a date, month, year or quarter from a calendar visualisation. You can choose the date format (how it will appear in the query string parameter). If you require another date format, contact ${supportEmail}`,
  },
  {
    name: 'Unique ID',
    value: 'unique',
    tooltip: 'A unique value autogenerated for each link.',
  },
  {
    name: 'Fixed',
    value: 'fixed',
    tooltip:
      'Text added to every link without the user having to select or enter a value on the **Track > Create links** page.',
  },
]

interface EditableParameterNameProps {
  parameter: ParamDefs
  isLocked: boolean
  setShowUnlockModal: () => void
  trackAction: (
    action: string,
    id: string,
    newVal: string,
    oldVal: string,
  ) => void
}

const EditableParameterName = ({
  parameter,
  isLocked,
  setShowUnlockModal,
  trackAction,
}: EditableParameterNameProps) => {
  const { fieldID, fieldName, required, fieldType, copyFromField } = parameter

  const [updateParamByID] = useMutation(updateGeneratorParameterByID, {
    refetchQueries: [getCampaignCodeGenerator],
  })

  const [paramName, setParamName] = useState(fieldName)

  return (
    <div className={styles.editableParameterName}>
      <ClickEditInput
        id={fieldID}
        name={fieldID}
        className={styles.parameterName}
        value={paramName}
        disableEditFn={isLocked ? () => setShowUnlockModal() : undefined}
        onChange={(e) => {
          const origVal = paramName

          setParamName(e)

          updateParamByID({
            variables: {
              updateFieldID: fieldID,
              fieldName: e,
            },
            refetchQueries: [getCampaignCodeGenerator],
          })

          trackAction('update-generator-parameter-name', fieldID, e, origVal)
        }}
      />
      {!required &&
        !['fixed', 'unique'].includes(fieldType) &&
        (!copyFromField || copyFromField.length === 0) && (
          <span className={styles.optional}>(optional)</span>
        )}
    </div>
  )
}

interface AccordionItemHeaderFieldsProps {
  parameter: ParamDefs
  validation: ValidationChecks[]
  isLocked: boolean
  setShowUnlockModal: () => void
  trackAction: (
    action: string,
    id: string,
    newVal: string,
    oldVal: string,
  ) => void
}

const AccordionItemHeaderFields = ({
  parameter,
  validation,
  isLocked,
  setShowUnlockModal,
  trackAction,
}: AccordionItemHeaderFieldsProps) => {
  const [updateParamByID] = useMutation(updateGeneratorParameterByID, {
    refetchQueries: [getCampaignCodeGenerator],
  })

  const history = useHistory()

  const [prefix, setPrefix] = useState(parameter.prefix)
  const [inputType, setInputType] = useState(parameter.fieldType)
  const [dateFormat, setDateFormat] = useState(
    parameter.dateFormat || 'DD-MM-YYYY',
  )
  const [fixedValue, setFixedValue] = useState<string | null>(
    parameter.fixedValue || null,
  )

  return (
    <>
      <div className={styles.paramHeaderPrefix}>
        <Label
          id={`${parameter.fieldID}-prefix`}
          className={styles.paramHeaderFieldLabel}
        >
          <Tooltip
            id={`${parameter.fieldID}-prefix-tooltip`}
            useIcon
            tooltipPosition="right"
            tooltipMessage="The query string parameter name which appears before the typed or selected value. Example: 'utm_campaign=' is the prefix for the Google Analytics campaign parameter. This may be a symbol when concatenating multiple inputs into one parameter."
          >
            Prefix
          </Tooltip>
        </Label>
        {parameter.metaParameter ? (
          <p className={styles.metaParameter}>Meta parameter</p>
        ) : (
          <ClickEditInput
            id={`${parameter.fieldID}-prefix`}
            name={parameter.fieldID}
            className={styles.parameterInput}
            value={prefix}
            disableEditFn={isLocked ? () => setShowUnlockModal() : undefined}
            placeholder="No prefix set"
            beforeChange={(inputValue: string) => {
              // Special characters must be allowed in prefixes. Only check for casing and spaces
              // TODO: Add new rule for special characters specifically in prefixes?
              const replaceSpaces = validation.find(
                (rule) => rule.name === 'REPLACE_SPACES_WITH',
              ) || {
                enabled: true,
                name: 'REPLACE_SPACES_WITH',
                value: '_',
              }

              const allLowerCase = validation.find(
                (rule) => rule.name === 'ALL_LOWER_CASE',
              ) || {
                enabled: true,
                name: 'ALL_LOWER_CASE',
                value: null,
              }

              const useValidation: ValidationChecks[] | null = [
                !allLowerCase.enabled
                  ? {
                      enabled:
                        typeof parameter.forceLowerCase === 'boolean'
                          ? parameter.forceLowerCase
                          : true,
                      name: 'ALL_LOWER_CASE',
                      value: null,
                    }
                  : allLowerCase,
                replaceSpaces,
              ]

              const preparedInput = prepareInput(inputValue, useValidation)

              return preparedInput
            }}
            onChange={(e) => {
              const origVal = prefix

              setPrefix(e)

              updateParamByID({
                variables: {
                  updateFieldID: parameter.fieldID,
                  prefix: e,
                },
              })

              trackAction(
                'update-generator-parameter-prefix',
                parameter.fieldID,
                e,
                origVal,
              )
            }}
          />
        )}
      </div>
      <div className={styles.paramHeaderInputType}>
        <Label
          id={`${parameter.fieldID}-inputType`}
          className={styles.paramHeaderFieldLabel}
        >
          <Tooltip
            id={`${parameter.fieldID}-only-show-if-tooltip`}
            useIcon
            tooltipPosition="right"
            tooltipMessage={
              <ul
                style={{
                  paddingInlineStart: 20,
                }}
              >
                <li>
                  <strong>Dropdown:</strong> Users select a value from a list of
                  options. Dropdown options are maintained by your admins on the
                  Track &gt; Edit dropdowns page.
                </li>
                <li>
                  <strong>Free text:</strong> Users can type or paste values,
                  with data validation rules selected by your admins.
                </li>
                <li>
                  <strong>Date:</strong> Users can select a date, month, year or
                  quarter from a calendar visualisation. You can choose the date
                  format (how it will appear in the query string parameter). If
                  you require another date format, contact
                  {supportEmail}
                </li>
              </ul>
            }
          >
            Input type
          </Tooltip>
        </Label>
        <SelectBox
          key={`${inputType}`}
          id={`${parameter.fieldID}-inputType`}
          className={styles.parameterInput}
          isSearchable={false}
          labelKey="name"
          value={inputTypesDropdown.find(
            (option) => option.value === inputType,
          )}
          options={inputTypesDropdown}
          onChange={(newValue) => {
            if (!newValue) return

            if (isLocked) {
              setShowUnlockModal()

              return
            }

            const { value } = newValue

            if (value !== '') {
              const origVal = inputType

              setInputType(value as InputTypes)

              const newParamType: any = {
                updateFieldID: parameter.fieldID,
                fieldType: value,
              }

              if (value === 'date') {
                newParamType.dateFormat = 'DD-MM-YYYY'
                setDateFormat('DD-MM-YYYY')
              }

              // Fixed parameters should not be case sensitive
              if (value === 'fixed') {
                newParamType.forceLowerCase = false
                newParamType.required = false
              }

              updateParamByID({
                variables: newParamType,
              })

              trackAction(
                'update-generator-parameter-input-type',
                parameter.fieldID,
                value,
                origVal,
              )
            }
          }}
        />
        {inputType === 'select' && (
          <NavigateButton
            className={styles.editDropdowns}
            onPress={() => {
              history.push(`/track/edit-dropdowns?fieldID=${parameter.fieldID}`)

              trackAction(
                'update-parameters-click-edit-dropdown',
                parameter.fieldID,
                parameter.fieldName,
                '',
              )
            }}
          >
            {parameter.selectFields && parameter.selectFields.length > 0
              ? 'Edit'
              : 'Add'}{' '}
            dropdowns
          </NavigateButton>
        )}
        {inputType === 'date' && (
          <SelectBoxSimple
            className={styles.parameterInput}
            name={`${parameter.fieldID}-dateFormats`}
            value={dateFormat}
            onChange={(val) => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              if (val !== '') {
                const origVal = dateFormat

                setDateFormat(val as DateFormats)

                updateParamByID({
                  variables: {
                    updateFieldID: parameter.fieldID,
                    dateFormat: val,
                  },
                })

                trackAction(
                  'update-generator-parameter-date-format',
                  parameter.fieldID,
                  val,
                  origVal,
                )
              }
            }}
          >
            {dateOptions.map((option) => {
              return (
                <option key={option.value} value={option.value}>
                  {option.value}
                </option>
              )
            })}
          </SelectBoxSimple>
        )}
        {inputType === 'fixed' && (
          <ClickEditInput
            className={classNames(styles.parameterInput, styles.fixedValue)}
            id="fixedValue"
            name="fixedValue"
            value={fixedValue || ''}
            disableEditFn={isLocked ? () => setShowUnlockModal() : undefined}
            onChange={(e) => {
              const origVal = fixedValue

              setFixedValue(e)

              updateParamByID({
                variables: {
                  updateFieldID: parameter.fieldID,
                  fixedValue: e,
                },
              })

              trackAction(
                'update-generator-parameter-fixed-value',
                parameter.fieldID,
                e,
                origVal || '',
              )
            }}
          />
        )}
      </div>
    </>
  )
}

interface ParameterSettingsProps {
  parameter: ParamDefs
  validation: ValidationChecks[]
  dropdownParams: ParamDefs[]
  copyFromValues: ParamDefs[]
  isLocked: boolean
  setShowUnlockModal: () => void
  setAccordionOpenState: React.Dispatch<React.SetStateAction<boolean[]>>
  trackAction: (
    action: string,
    id: string,
    newVal: string,
    oldVal: string,
  ) => void
}

const ParameterSettings = ({
  parameter,
  validation,
  dropdownParams,
  copyFromValues,
  isLocked,
  setShowUnlockModal,
  setAccordionOpenState,
  trackAction,
}: ParameterSettingsProps) => {
  const {
    fieldID,
    fieldType,
    required,
    helpText,
    hideInTrackView,
    forceLowerCase,
    metaParameter,
    selectFields,
    copyFromField,
    emailDefault,
    parameterDependsOn,
    isCreativeField,
    lengthLimit = 45,
  } = parameter

  const linkCopy = useReactiveVar(linkOrCode)

  const isMobile = useMobile(1200)

  const { fullOnboardingSections, updateOnboardingSection } = useOnboarding()

  const [updateParamByID] = useMutation(updateGeneratorParameterByID, {
    refetchQueries: [getCampaignCodeGenerator],
  })
  const [deleteParamByID, { loading: deletingParameter }] = useMutation(
    deleteGeneratorParameterByID,
  )

  const [paramFields, setParamFields] = useState({
    required,
    helpText,
    hideInTrackView,
    forceLowerCase,
    metaParameter,
    copyFromField: copyFromField || [],
    emailDefault: emailDefault?.optionID,
    onlyShowIf: parameterDependsOn
      ? {
          parentField: parameterDependsOn.parentFieldID,
          parentValues: parameterDependsOn.parentOptionIDs,
        }
      : null,
    isCreativeField,
    lengthLimit,
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const parentFieldOptions = useMemo(() => {
    const allOptions = dropdownParams
      .filter((param) => {
        const hasCircularDependency = checkCircularDependency(
          param,
          dropdownParams,
          [parameter.fieldID],
        )

        return param.fieldID !== parameter.fieldID && !hasCircularDependency
      })
      .map((param) => ({
        fieldID: param.fieldID,
        fieldName: param.fieldName,
      }))

    // Add workspace restrictions
    allOptions.unshift({
      fieldID: 'account',
      fieldName: 'Workspace',
    })

    return allOptions
  }, [parameter, dropdownParams])

  const completeOnboardingSection = useCallback(() => {
    const editTaxonomy = fullOnboardingSections.account.find(
      (section) => section.onboardingSectionID === 'editTaxonomy',
    )

    if (editTaxonomy && !editTaxonomy.sectionCompleted)
      updateOnboardingSection('editTaxonomy', 'account')
  }, [fullOnboardingSections])

  const globalLowerCaseEnabled = useMemo(() => {
    const globalLowerCaseRule = validation.find(
      (rule) => rule.name === 'ALL_LOWER_CASE',
    )

    if (!globalLowerCaseRule) return true

    return globalLowerCaseRule.enabled
  }, [validation])

  return (
    <>
      <div className={styles.helpTextContainer}>
        <Label id="helpText" className={styles.parameterRuleLabel}>
          <Tooltip
            id={`${fieldID}-description-tooltip`}
            useIcon
            maxWidth={300}
            tooltipPosition={isMobile ? 'bottom' : 'right'}
            tooltipMessage="Text designed to help users enter the right values on the Track>Create links page. Text will show when the users hovers over a tooltip next to each parameter name."
          >
            Description
          </Tooltip>
        </Label>
        <ClickEditInput
          id="helpText"
          name="helpText"
          className={classNames(styles.parameterInput, styles.fullWidth)}
          value={paramFields.helpText}
          disableEditFn={isLocked ? () => setShowUnlockModal() : undefined}
          onChange={(e) => {
            const origVal = helpText

            setParamFields((curr) => ({ ...curr, helpText: e }))

            updateParamByID({
              variables: {
                updateFieldID: fieldID,
                helpText: e,
              },
            })

            trackAction(
              'update-generator-parameter-help-text',
              fieldID,
              e,
              origVal || '',
            )
          }}
        />
      </div>
      <Row align="flex-start" className={styles.extraDetailsRow}>
        {!['unique', 'fixed'].includes(fieldType) &&
          (!copyFromField || copyFromField.length === 0) && (
            <>
              <div className={styles.checkboxField}>
                <Input
                  type="checkbox"
                  name="required"
                  label="Required"
                  checked={paramFields.required}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    if (isLocked) {
                      setShowUnlockModal()

                      return
                    }

                    const { checked } = e.target as HTMLInputElement

                    const origVal = required

                    setParamFields((curr) => ({ ...curr, required: checked }))

                    updateParamByID({
                      variables: {
                        updateFieldID: fieldID,
                        required: checked,
                      },
                    })

                    trackAction(
                      'update-generator-parameter-required',
                      fieldID,
                      checked ? 'true' : 'false',
                      origVal ? 'true' : 'false',
                    )
                  }}
                />
                <Tooltip
                  id={`${fieldID}-required-tooltip`}
                  useIcon
                  tooltipPosition={isMobile ? 'bottom' : 'right'}
                  tooltipMessage={`If checked, users will have to complete this field to create a ${linkCopy}.`}
                />
              </div>
              <div className={styles.separator} />
              <div className={styles.checkboxField}>
                <Input
                  type="checkbox"
                  name="forceLowercase"
                  label="Lowercase"
                  checked={
                    !!paramFields.forceLowerCase || globalLowerCaseEnabled
                  }
                  disabled={globalLowerCaseEnabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (isLocked) {
                      setShowUnlockModal()

                      return
                    }

                    const origVal = forceLowerCase

                    const { checked } = e.target as HTMLInputElement

                    setParamFields((curr) => ({
                      ...curr,
                      forceLowerCase: checked,
                    }))

                    updateParamByID({
                      variables: {
                        updateFieldID: fieldID,
                        forceLowerCase: checked,
                      },
                    })

                    trackAction(
                      'update-generator-parameter-lowercase',
                      fieldID,
                      checked ? 'true' : 'false',
                      origVal ? 'true' : 'false',
                    )
                  }}
                />
                <Tooltip
                  id={`${fieldID}-lowercase-tooltip`}
                  useIcon
                  maxWidth={400}
                  tooltipPosition={isMobile ? 'bottom' : 'right'}
                  tooltipMessage={
                    <>
                      <p>
                        If checked, text will be automatically lowercased. This
                        makes data easier to read in analytics platforms.
                      </p>
                      {globalLowerCaseEnabled && (
                        <p>
                          <strong>
                            This cannot be unchecked while 'All parameters
                            lowercase only' is checked in Advanced rules &gt;
                            Parameter validation options below.
                          </strong>
                        </p>
                      )}
                    </>
                  }
                />
              </div>
              <div className={styles.separator} />
            </>
          )}
        {(!copyFromField || copyFromField.length === 0) &&
          ['select', 'input'].includes(fieldType) && (
            <>
              <div className={styles.characterLimitField}>
                <Tooltip
                  id={`${fieldID}-character-limit-tooltip`}
                  className={styles.characterLimitTooltip}
                  useIcon
                  maxWidth={300}
                  tooltipPosition={isMobile ? 'bottom' : 'right'}
                  tooltipMessage="Max characters a user can type in this parameter before we block the link being created. Used to make parameters fit in other systems with strict limits."
                >
                  Character limit
                </Tooltip>
                <ClickEditInput
                  id="character-limit-input"
                  name="character-limit-input"
                  className={styles.clickEditField}
                  value={(lengthLimit as number).toString()}
                  disableEditFn={
                    isLocked ? () => setShowUnlockModal() : undefined
                  }
                  beforeChange={(inputValue: string) =>
                    inputValue.replaceAll(/[^0-9]/g, '').slice(0, 3)
                  }
                  onChange={(newValue) => {
                    setParamFields((curr) => ({
                      ...curr,
                      lengthLimit: parseInt(newValue, 10),
                    }))

                    updateParamByID({
                      variables: {
                        updateFieldID: fieldID,
                        lengthLimit: parseInt(newValue, 10),
                      },
                    })

                    trackAction(
                      'update-generator-parameter-max-length',
                      fieldID,
                      (lengthLimit as number).toString(),
                      newValue,
                    )
                  }}
                />
              </div>
              <div className={styles.separator} />
            </>
          )}
        <div className={styles.checkboxField}>
          <Input
            type="checkbox"
            name="metaParameter"
            label="Meta"
            checked={!!paramFields.metaParameter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              const origVal = metaParameter

              const { checked } = e.target as HTMLInputElement

              setParamFields((curr) => ({ ...curr, metaParameter: checked }))

              updateParamByID({
                variables: {
                  updateFieldID: fieldID,
                  metaParameter: checked,
                },
              })

              trackAction(
                'update-generator-parameter-meta',
                fieldID,
                checked ? 'true' : 'false',
                origVal ? 'true' : 'false',
              )
            }}
          />
          <Tooltip
            id={`${fieldID}-meta-tooltip`}
            useIcon
            maxWidth={300}
            tooltipPosition={isMobile ? 'bottom' : 'right'}
            tooltipMessage={`If checked, this parameter data will only be stored in this platform and will not be visible in your ${linkCopy} (or other analytics tools). Designed for notes.`}
          />
        </div>
        {['select', 'input'].includes(fieldType) && (
          <>
            <div className={styles.separator} />
            <div className={styles.checkboxField}>
              <Input
                type="checkbox"
                name="isCreativeField"
                label="Allow creatives"
                checked={!!paramFields.isCreativeField}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  if (isLocked) {
                    setShowUnlockModal()

                    return
                  }

                  const origVal = isCreativeField
                  const { checked } = e.target as HTMLInputElement

                  setParamFields((curr) => ({
                    ...curr,
                    isCreativeField: checked,
                  }))

                  updateParamByID({
                    variables: {
                      updateFieldID: fieldID,
                      isCreativeField: checked,
                    },
                  })

                  trackAction(
                    'update-generator-parameter-is-creative-field',
                    fieldID,
                    checked ? 'true' : 'false',
                    origVal ? 'true' : 'false',
                  )
                }}
              />
              <Tooltip
                id={`${fieldID}-isCreativeField-tooltip`}
                useIcon
                maxWidth={300}
                tooltipPosition={isMobile ? 'bottom' : 'right'}
                tooltipMessage={
                  <p>
                    If checked, users can upload ad creatives for values in this
                    parameter. The creatives will be visible on the{' '}
                    <BoxedText>Report &gt;Marketing journeys</BoxedText>
                    page.
                  </p>
                }
              />
            </div>
          </>
        )}
      </Row>
      <Row align="flex-start" className={styles.extraDetailsRow}>
        <div className={styles.checkboxField}>
          <Input
            type="checkbox"
            name="hideInTrackView"
            label={<p>Show as column</p>}
            checked={!paramFields.hideInTrackView}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              const origVal = hideInTrackView
              const { checked } = e.target as HTMLInputElement

              setParamFields((curr) => ({
                ...curr,
                hideInTrackView: !checked,
              }))

              updateParamByID({
                variables: {
                  updateFieldID: fieldID,
                  hideInTrackView: !checked,
                },
              })

              trackAction(
                'update-generator-parameter-is-creative-field',
                fieldID,
                checked ? 'true' : 'false',
                origVal ? 'true' : 'false',
              )
            }}
          />
          <Tooltip
            id={`${fieldID}-hideInTrackView-tooltip`}
            useIcon
            maxWidth={300}
            tooltipPosition={isMobile ? 'bottom' : 'right'}
            tooltipMessage={
              <p>
                Show this parameter in the{' '}
                <BoxedText>
                  {dynamicTextValues.trackViewPage[linkCopy]}
                </BoxedText>{' '}
                table as an editable column. Turn off for fixed or macro
                parameters which don't change to simplify the table.
              </p>
            }
          />
        </div>
      </Row>
      {/* copyFromField and emailDefault should not be possible to set at the same time */}
      {fieldType === 'input' && !emailDefault && (
        <Row align="flex-start" className={styles.extraDetailsRow}>
          <Label
            id={`${fieldID}-copyFrom`}
            className={styles.parameterRuleLabel}
          >
            <Tooltip
              id={`${fieldID}-copy-from-tooltip`}
              useIcon
              tooltipPosition={isMobile ? 'bottom' : 'right'}
              tooltipMessage="This parameter will take the values given to the selected parameters in this dropdown, separated by the value you give in the Advanced rules. It will not show in the form on Track>Create links, as its value is auto-generated."
            >
              Copy from
            </Tooltip>
          </Label>
          <SelectBoxChecklist
            id={`${fieldID}-copyFrom`}
            labelKey="fieldName"
            valueKey="fieldID"
            value={copyFromValues.filter((option) =>
              paramFields.copyFromField.find(
                ({ copyFromID }) => copyFromID === option.fieldID,
              ),
            )}
            options={copyFromValues}
            onChange={(newValue) => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              const existingCopyFrom = paramFields.copyFromField

              const newCopyFrom: {
                copyFromID: string
                truncateToLength?: number | null
              }[] = newValue.map((val) => ({
                copyFromID: val.fieldID,
              }))

              if (existingCopyFrom.length > 0) {
                existingCopyFrom.forEach((existing) => {
                  const found = newCopyFrom.findIndex(
                    (newCopy) => newCopy.copyFromID === existing.copyFromID,
                  )

                  if (found !== -1) {
                    newCopyFrom.splice(found, 1, { ...existing })
                  }
                })
              }

              setParamFields((curr) => {
                return {
                  ...curr,
                  copyFromField: newCopyFrom,
                  required:
                    curr.required && newValue.length > 0
                      ? false
                      : curr.required,
                  forceLowerCase: false,
                  onlyShowIf:
                    newValue.length > 0 && curr.onlyShowIf
                      ? null
                      : curr.onlyShowIf,
                }
              })

              updateParamByID({
                variables: {
                  updateFieldID: fieldID,
                  required: false,
                  forceLowerCase: false,
                  copyFromField: newCopyFrom.map(
                    ({ copyFromID, truncateToLength }) => ({
                      copyFromID,
                      truncateToLength,
                    }),
                  ),
                  parameterDependsOn:
                    newValue.length > 0 && paramFields.onlyShowIf
                      ? null
                      : undefined,
                },
              })

              trackAction(
                'update-generator-parameter-copy-from-fields',
                fieldID,
                JSON.stringify(newCopyFrom.map((val) => val.copyFromID)),
                JSON.stringify(existingCopyFrom.map((val) => val.copyFromID)),
              )
            }}
          />
        </Row>
      )}
      {!emailDefault &&
        fieldType === 'input' &&
        paramFields.copyFromField.length > 0 && (
          <Row align="flex-start" className={styles.extraDetailsRow}>
            <Label
              id={`${fieldID}-copyFrom-truncate`}
              className={styles.parameterRuleLabel}
            >
              <Tooltip
                id={`${fieldID}-copy-from-truncate-tooltip`}
                className={styles.characterLimitTooltip}
                useIcon
                maxWidth={300}
                tooltipPosition={isMobile ? 'bottom' : 'right'}
                tooltipMessage="Limit copied parameters to a certain number of characters to make short codes."
              >
                Truncate
              </Tooltip>
            </Label>
            <div className={styles.copyFromLimiters}>
              {paramFields.copyFromField.map(
                ({ copyFromID, truncateToLength }, index) => {
                  const copyFromFull = copyFromValues.find(
                    (copyFromValue) => copyFromValue.fieldID === copyFromID,
                  )

                  if (!copyFromFull) return null

                  return (
                    <>
                      {index > 0 && <div className={styles.separator} />}
                      <Label id={`truncate-${copyFromID}`}>
                        {copyFromFull.fieldName}
                      </Label>
                      <ClickEditInput
                        id={`truncate-${copyFromID}`}
                        name={`truncate-${copyFromID}`}
                        className={styles.clickEditField}
                        value={truncateToLength?.toString() || undefined}
                        placeholder="N/A"
                        disableEditFn={
                          isLocked ? () => setShowUnlockModal() : undefined
                        }
                        beforeChange={(inputValue: string) =>
                          inputValue.replaceAll(/[^0-9]/g, '').slice(0, 3)
                        }
                        onChange={(newValue) => {
                          const newCopyFromField = _.cloneDeep(
                            paramFields.copyFromField,
                          )

                          const newCopyFromIndex = newCopyFromField.findIndex(
                            (val) => val.copyFromID === copyFromID,
                          )

                          newCopyFromField?.splice(newCopyFromIndex, 1, {
                            copyFromID,
                            truncateToLength: parseInt(newValue, 10),
                          })

                          setParamFields((curr) => ({
                            ...curr,
                            copyFromField: newCopyFromField || [],
                          }))

                          updateParamByID({
                            variables: {
                              updateFieldID: fieldID,
                              copyFromField: newCopyFromField.map(
                                ({ copyFromID: c, truncateToLength: t }) => ({
                                  copyFromID: c,
                                  truncateToLength: t,
                                }),
                              ),
                            },
                          })

                          trackAction(
                            'update-generator-parameter-copy-from-truncate',
                            fieldID,
                            `${copyFromID}: ${
                              truncateToLength?.toString() || 'N/A'
                            }`,
                            `${copyFromID}: ${newValue}`,
                          )
                        }}
                      />
                    </>
                  )
                },
              )}
            </div>
          </Row>
        )}
      {((fieldType === 'input' &&
        (!copyFromField || copyFromField.length === 0)) ||
        fieldType === 'select') && (
        <Row align="flex-start" className={styles.extraDetailsRow}>
          <Label
            id={`${fieldID}-emailDefault`}
            className={styles.parameterRuleLabel}
          >
            <Tooltip
              id={`${fieldID}-emailDefault-tooltip`}
              useIcon
              tooltipPosition={isMobile ? 'bottom' : 'right'}
              tooltipMessage={
                <>
                  The preselected value when you find and replace email links in{' '}
                  <BoxedText>
                    {dynamicTextValues.trackCreatePage[linkCopy]}
                  </BoxedText>
                  'Email' tab.
                </>
              }
            >
              Email default
            </Tooltip>
          </Label>
          {fieldType === 'select' && selectFields ? (
            <SelectBox
              id={`${fieldID}-emailDefault`}
              className={styles.emailDefaultField}
              labelKey="optionName"
              valueKey="optionID"
              value={[
                {
                  optionID: null,
                  optionName: 'None (Do not set)',
                  optionValue: null,
                },
                ...selectFields,
              ].find((option) => option.optionID === emailDefault)}
              options={[
                {
                  optionID: null,
                  optionName: 'None (Do not set)',
                  optionValue: null,
                },
                ...selectFields,
              ]}
              onChange={(newValue) => {
                if (isLocked) {
                  setShowUnlockModal()

                  return
                }

                setParamFields((curr) => ({
                  ...curr,
                  emailDefault: newValue?.optionID,
                }))

                if (!newValue || newValue.optionID === null) {
                  updateParamByID({
                    variables: {
                      updateFieldID: fieldID,
                      emailDefault: null,
                    },
                  })

                  return
                }

                const newDefault = selectFields?.find(
                  (field) => field.optionID === newValue.optionID,
                )

                if (newDefault) {
                  updateParamByID({
                    variables: {
                      updateFieldID: fieldID,
                      emailDefault: {
                        parameterID: fieldID,
                        optionID: newDefault.optionID,
                        optionName: newDefault.optionName,
                        optionValue: newDefault.optionValue,
                      },
                    },
                  })
                }
              }}
            />
          ) : (
            <ClickEditInput
              showCancel
              className={styles.emailDefaultField}
              id={`${parameter.fieldID}-emailDefault`}
              name={`${parameter.fieldID}-emailDefault`}
              value={paramFields.emailDefault || ''}
              disableEditFn={isLocked ? () => setShowUnlockModal() : undefined}
              beforeChange={(inputValue) => {
                if (metaParameter !== true) {
                  return prepareInput(inputValue, validation)
                }

                return inputValue
              }}
              onChange={(e) => {
                setParamFields((curr) => ({ ...curr, emailDefault: e }))

                updateParamByID({
                  variables: {
                    updateFieldID: fieldID,
                    emailDefault: e
                      ? {
                          parameterID: fieldID,
                          optionID: e,
                          optionName: e,
                          optionValue: e,
                        }
                      : null,
                  },
                })
              }}
            />
          )}
        </Row>
      )}
      {parentFieldOptions.length > 0 && (
        <Row
          align="flex-start"
          className={styles.extraDetailsRow}
          style={{ marginBottom: 0 }}
        >
          <OnlyShowIf
            fieldID={fieldID}
            parameterDependsOn={parameterDependsOn}
            parentFieldOptions={parentFieldOptions}
            dropdownParams={dropdownParams}
            onlyShowIf={paramFields.onlyShowIf}
            setOnlyShowIf={(newValue) =>
              setParamFields((curr) => ({ ...curr, onlyShowIf: newValue }))
            }
            isLocked={isLocked}
            setShowUnlockModal={setShowUnlockModal}
            trackAction={trackAction}
            updateParamByID={updateParamByID}
          />
        </Row>
      )}
      <DeleteButton
        className={styles.deleteParameterButton}
        onPress={() => {
          if (isLocked) {
            setShowUnlockModal()

            return
          }

          setShowDeleteModal(true)
        }}
      />
      {showDeleteModal && (
        <Modal
          setIsOpen={setShowDeleteModal}
          headerColor="pink"
          modalHeader="Remove parameter"
          isWarning
          noText="Cancel"
          yesText="Confirm delete"
          yesButtonLoading={deletingParameter}
          onYes={async () => {
            await deleteParamByID({
              variables: {
                removeFieldID: fieldID,
              },
            })

            setShowDeleteModal(false)

            setAccordionOpenState((curr) => curr.slice(1).map((p) => false))

            trackAction(
              'delete-generator-parameter',
              parameter.fieldID,
              '',
              JSON.stringify(parameter),
            )

            completeOnboardingSection()
          }}
        >
          <p>
            Are you sure you want to permanently delete the{' '}
            <strong>{parameter.fieldName}</strong> parameter?
          </p>
          <p>
            This will remove all names, values and codes and cannot be reversed.
          </p>
          <p>
            Existing {linkCopy}s will still contain the parameter values,
            however you will not be able to filter reports on the deleted
            parameter.
          </p>
        </Modal>
      )}
    </>
  )
}

export interface ParametersDragAndDropContainerProps {
  isLocked: boolean
  setShowUnlockModal: () => void
  validation: GetCampaignCodeGeneratorQuery['campaignCodeGenerator']['validationChecks']
  parameters: GetCampaignCodeGeneratorQuery['campaignCodeGenerator']['paramDefs']
  paramColors: ParamColors | null
  setHoverItem: Dispatch<SetStateAction<string | null>>
  showUplifterIDParam: boolean
}

export const ParametersDragAndDropContainer = ({
  isLocked = false,
  setShowUnlockModal,
  validation,
  parameters,
  paramColors,
  setHoverItem,
  showUplifterIDParam,
}: ParametersDragAndDropContainerProps) => {
  const { workspaceID } = useReactiveVar(currentUserDetails)

  const linkCopy = useReactiveVar(linkOrCode)

  const logAction = useLogAction()

  const [__, { refetch: refetchGenerator }] = useLazyQuery(
    getCampaignCodeGenerator,
  )

  const [updateParamsOrder] = useMutation(updateGeneratorParameterOrder)
  const [
    addNewParam,
    { loading: addingNewParam, error: addParamError },
  ] = useMutation(addNewGeneratorParameter)
  const [addMutlipleDropdownOptions] = useMutation(
    updateGeneratorParamAddMultipleSelectOptions,
  )

  const { fullOnboardingSections, updateOnboardingSection } = useOnboarding()

  const isMobile = useMobile(850)

  const [orderedParamIds, setOrderedParamIds] = useState(
    parameters.map(({ fieldID }) => fieldID),
  )
  const [accordionOpenState, setAccordionOpenState] = useState(
    parameters.map((p) => false),
  )
  const [showModal, setShowModal] = useState(false)

  const dropdownParams = useMemo(() => {
    return parameters.filter((param) => param.fieldType === 'select')
  }, [parameters])

  const completeOnboardingSection = useCallback(() => {
    const editTaxonomy = fullOnboardingSections.account.find(
      (section) => section.onboardingSectionID === 'editTaxonomy',
    )

    if (editTaxonomy && !editTaxonomy.sectionCompleted)
      updateOnboardingSection('editTaxonomy', 'account')
  }, [fullOnboardingSections])

  const trackAction = useCallback(
    (action: string, id: string, newVal: string, oldVal: string) => {
      logAction({
        variables: {
          action,
          extra: JSON.stringify({
            id,
            newVal,
            oldVal,
          }),
          websiteSection: 'track',
          pagePath: '/track/edit-parameters-and-rules',
          functionName: 'updateParameter',
        },
      })
    },
    [],
  )

  const mapParams = useCallback(
    (param: TemplateParam) => {
      const {
        fieldName,
        fieldType,
        required,
        prefix,
        helpText,
        optionList,
        metaParameter,
        precedingParamPrefix,
        forceLowerCase,
      } = param

      return (
        <DropdownButtonItem
          key={fieldName}
          isDisabled={addingNewParam}
          className={isLocked ? styles.lockedButton : undefined}
          onPress={async () => {
            if (isLocked) {
              setShowUnlockModal()

              return
            }

            // Used to automatically open the param once it's added
            let paramIndexToOpen = parameters.length

            let newFieldName = fieldName
            let i = 0

            // Make sure there are no duplicate names
            const paramNames = parameters.map((p) => p.fieldName)

            while (i < parameters.length) {
              if (paramNames.indexOf(newFieldName) === -1) {
                i = parameters.length
                break
              }

              i += 1
              newFieldName = `${fieldName} (${i})`
            }

            const { data } = await addNewParam({
              variables: {
                fieldName: newFieldName,
                fieldAvailable: true,
                fieldType,
                required,
                prefix,
                helpText,
                metaParameter,
                forceLowerCase,
              },
            })

            if (data) {
              const {
                track: {
                  addGeneratorParameter: { fieldID },
                },
              } = data

              if (precedingParamPrefix) {
                const precedingParamIndex = parameters.findIndex(
                  (p) => p.prefix === precedingParamPrefix,
                )

                if (precedingParamIndex !== -1) {
                  const newParamsOrder = [
                    ...parameters.map((parameter) => parameter.fieldID),
                  ]

                  paramIndexToOpen = precedingParamIndex + 1

                  newParamsOrder.splice(precedingParamIndex + 1, 0, fieldID)

                  await updateParamsOrder({
                    variables: {
                      newOrder: newParamsOrder,
                    },
                  })
                }
              }

              if (optionList) {
                // Don't need to refetch generator in this case
                // Because the whole generator is returned in addMultipleDropdownOptions mutation
                // It shouldn't be
                // Returning only the updated parameter (ParamDef) would be better
                // Then updating the cached value directly
                // See the TODO item below
                await addMutlipleDropdownOptions({
                  variables: {
                    fieldID,
                    optionList,
                  },
                })
              }

              // Expand newly added parameter
              setAccordionOpenState(
                Array.from(
                  new Array(parameters.length + 1),
                  (p, paramIndex) => paramIndex === paramIndexToOpen,
                ),
              )

              if (window.dataLayer && window.dataLayer.push) {
                window.dataLayer.push({
                  event: 'add-generator-parameter-template',
                  paramName: fieldName,
                })
              }

              logAction({
                variables: {
                  action: 'add-generator-parameter-template',
                  extra: fieldName,
                  websiteSection: 'track',
                  pagePath: '/track/edit-parameters-and-rules',
                  functionName: 'addNewParameter',
                },
              })

              if (!optionList && !precedingParamPrefix) {
                // Refresh the full generator
                // TODO: Improve cache
                // Every ParamDef could be its own cache object
                // Items should be read from it
                // Separate queries for getting validationChecks and paramDefs
                await refetchGenerator()
              }
            }
          }}
        >
          <Tooltip
            id={`${fieldName}-field-name-tooltip`}
            tooltipMessage={
              <>
                <p>{helpText}</p>
                {isLocked && (
                  <p className={styles.lockedTooltip}>
                    Unlock the page to add a new parameter.
                  </p>
                )}
              </>
            }
            useIcon
            maxWidth={250}
          >
            {fieldName}
          </Tooltip>
        </DropdownButtonItem>
      )
    },
    [parameters, isLocked, addingNewParam],
  )

  const addDropdownsButton = useMemo(
    () => (
      <ButtonDropdown
        variant="secondary"
        buttonText={
          parameters.length > 0
            ? 'Add parameter'
            : 'Add a parameter to get started'
        }
        containerClassName={styles.addNew}
        dropdownClassName={styles.addNewDropdown}
        loading={addingNewParam}
      >
        {[
          <DropdownButtonItem
            key="Custom"
            className={isLocked ? styles.lockedButton : undefined}
            onPress={() => {
              if (isLocked) {
                setShowUnlockModal()

                return
              }

              setShowModal(true)
            }}
          >
            <Tooltip
              id="add-parameter-tooltip"
              tooltipMessage={
                <>
                  <p>
                    Create a new custom parameter by choosing the name, prefix,
                    input type and rules.
                  </p>
                  {isLocked && (
                    <p className={styles.lockedTooltip}>
                      Unlock the page to add a new custom parameter.
                    </p>
                  )}
                </>
              }
              useIcon
              maxWidth={250}
            >
              Custom
            </Tooltip>
          </DropdownButtonItem>,
          <DropdownLabel key="popularLabel">Popular</DropdownLabel>,
          ...Object.values(popularParameters).map(mapParams),
          <DropdownLabel key="GA4Label">
            New Google Analytics 4 UTMs
          </DropdownLabel>,
          ...Object.values(ga4Parameters).map(mapParams),
        ]}
      </ButtonDropdown>
    ),
    [parameters, addingNewParam, isLocked, mapParams],
  )

  // Update IDs whenever parameters changes
  useEffect(() => {
    setOrderedParamIds(parameters.map(({ fieldID }) => fieldID))
  }, [parameters])

  const accordionItems = useMemo(() => {
    const paramItems: (AccordionItemElement | null)[] = orderedParamIds.map(
      (paramId) => {
        const parameter = parameters.find(({ fieldID }) => paramId === fieldID)

        if (!parameter) return null

        return {
          key: parameter.fieldID,
          header: {
            title: (
              <EditableParameterName
                parameter={parameter}
                isLocked={isLocked}
                setShowUnlockModal={setShowUnlockModal}
                trackAction={trackAction}
              />
            ),
            rightContent: (
              <AccordionItemHeaderFields
                parameter={parameter}
                validation={validation}
                isLocked={isLocked}
                setShowUnlockModal={setShowUnlockModal}
                trackAction={trackAction}
              />
            ),
          },
          expandedContent: (
            <ParameterSettings
              parameter={parameter}
              validation={validation}
              dropdownParams={dropdownParams as ParamDefs[]}
              copyFromValues={getCopyFromValues(parameters, parameter)}
              isLocked={isLocked}
              setShowUnlockModal={setShowUnlockModal}
              setAccordionOpenState={setAccordionOpenState}
              trackAction={trackAction}
            />
          ),
        }
      },
    )

    return paramItems.filter((item) => !!item) as AccordionItemElement[]
  }, [
    paramColors,
    orderedParamIds,
    parameters,
    validation,
    trackAction,
    isLocked,
  ])

  return (
    <>
      <Row align="flex-start" className={styles.parametersHeaderRow}>
        <div className={classNames(styles.parameterHeading, styles.column1)}>
          <Tooltip
            id="parameter-header-tooltip"
            useIcon
            tooltipPosition="right"
            tooltipMessage={`The elements added to the end of your ${
              linkCopy === 'link' ? 'link after the landing page URL' : 'code'
            }.`}
          >
            <Heading type={2} align="left">
              Parameter
            </Heading>
          </Tooltip>
          {!isMobile && parameters.length > 0 && addDropdownsButton}
        </div>
        <div
          className={classNames(
            styles.parameterHeading,
            styles.secondaryColumnHeader,
          )}
        >
          <Tooltip
            id="parameter-prefix-header-tooltip"
            useIcon
            tooltipPosition="bottom"
            tooltipMessage="The parameter name before the typed or selected value. Example: 'utm_campaign=' is the prefix for the Google Analytics campaign parameter. Use a symbol to concatenate multiple inputs into one parameter."
          >
            <Heading type={2} align="left">
              Prefix
            </Heading>
          </Tooltip>
        </div>

        <div className={classNames(styles.parameterHeading)}>
          <Tooltip
            id="parameter-type-header-tooltip"
            useIcon
            maxWidth={300}
            tooltipPosition="bottom"
            tooltipMessage={
              <ul style={{ paddingInlineStart: 20 }}>
                <li>
                  <strong>Dropdown:</strong> Users select a value from a list of
                  options. Dropdown options are maintained by your Uplifter
                  admins on the Track &gt; Edit dropdowns page.
                </li>
                <li>
                  <strong>Free text:</strong> Users can type or paste values,
                  with data validation rules selected by your admins on the
                  Track &gt; Edit parameters page.
                </li>
                <li>
                  <strong>Date:</strong> Users can select a date, month, year or
                  quarter from a calendar visualisation. You can choose the date
                  format (how it will appear in the query string parameter). If
                  you require another date format, contact
                  {supportEmail}
                </li>
              </ul>
            }
          >
            <Heading type={2} align="left">
              Input type
            </Heading>
          </Tooltip>
        </div>
        {isMobile ? (
          <>{parameters.length > 0 && addDropdownsButton}</>
        ) : (
          <div className={classNames(styles.parameterHeading)}>
            <Tooltip
              id="parameter-more-header-tooltip"
              useIcon
              tooltipPosition="left"
              tooltipMessage="The data validation rules and options for each parameter, click on '⌄' to expand."
            >
              <Heading type={2} align="left">
                More
              </Heading>
            </Tooltip>
          </div>
        )}
      </Row>
      <div className={styles.dragAndDropParent}>
        {parameters.length > 3 && (
          <div className={styles.dragReorderCopy}>
            <span>To reorder, drag and drop</span>
            <BurgerIcon className={styles.copyBurger} />
          </div>
        )}
        <div
          className={classNames(styles.dragAndDropContainer, {
            [styles.hasUplifterID]: showUplifterIDParam,
          })}
        >
          {parameters.length === 0 ? (
            <div className={styles.noParamsButtonContainer}>
              {addDropdownsButton}
            </div>
          ) : (
            <>
              <Accordion
                id="parameters"
                className={
                  showUplifterIDParam ? styles.hasUplifterID : undefined
                }
                headerClassName={styles.parametersAccordionHeader}
                expandedContentClassName={styles.parametersSettings}
                onlyExpandOnArrowClick
                initialOpenState={accordionOpenState}
                accordionItems={accordionItems}
                onToggle={(itemIndex, state) =>
                  setAccordionOpenState((curr) =>
                    curr.map((p, i) => i === itemIndex && state === 'open'),
                  )
                }
                onMouseEnter={(key) => setHoverItem(key)}
                onMouseLeave={() => setHoverItem(null)}
                onDragEnd={async (result) => {
                  const { destination, source, draggableId } = result

                  if (!destination) return

                  if (
                    destination.droppableId === source.droppableId &&
                    destination.index === source.index
                  ) {
                    return
                  }

                  const paramIdsOrdered = parameters.map(
                    (parameter) => parameter.fieldID,
                  )

                  const newParamIdsOrdered = Array.from(paramIdsOrdered)
                  const oldParamIdsOrdered = Array.from(paramIdsOrdered)

                  newParamIdsOrdered.splice(source.index, 1)
                  newParamIdsOrdered.splice(destination.index, 0, draggableId)

                  const paramIDsMap = new Map(
                    newParamIdsOrdered.map((id, index) => [id, index]),
                  )

                  setOrderedParamIds(newParamIdsOrdered)

                  await updateParamsOrder({
                    variables: { newOrder: newParamIdsOrdered },
                    optimisticResponse: {
                      track: {
                        updateGeneratorParameterOrder: {
                          accountID: workspaceID,
                          // @ts-ignore
                          paramDefs: _.cloneDeep(parameters).sort((a, b) => {
                            return (
                              (paramIDsMap.get(a.fieldID) || 0) -
                              (paramIDsMap.get(b.fieldID) || 0)
                            )
                          }),
                          __typename: 'CampaignCodeGenerator',
                        },
                        __typename: 'TrackMutations',
                      },
                    },
                    refetchQueries: [getCampaignCodeGenerator],
                  })

                  completeOnboardingSection()

                  trackAction(
                    'update-generator-parameter-reorder-options',
                    'all',
                    JSON.stringify(newParamIdsOrdered),
                    JSON.stringify(oldParamIdsOrdered),
                  )
                }}
                dragHandleColors={paramColors}
              />
              {showUplifterIDParam && (
                <div
                  className={styles.uplifterIDParamContainer}
                  onMouseEnter={() => setHoverItem('uplifterID')}
                  onMouseLeave={() => setHoverItem(null)}
                >
                  <LockedIcon
                    color={paramColors ? paramColors.uplifterID : '#007d9b'}
                    className={styles.lockIcon}
                  />
                  <Heading
                    type={2}
                    align="left"
                    className={styles.uplifterIDTitle}
                  >
                    <Tooltip
                      id="uplifterID-tooltip"
                      tooltipMessage="A unique ID added to the end of every link. It allows us to more accurately integrate with Google Analytics and other platforms. It can be turned off in Advanced options below (not recommended)."
                      useIcon
                    >
                      Smart Link ID
                    </Tooltip>
                  </Heading>
                  <p className={styles.metaParameter}>&up_id=</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {addParamError && (
        <p className={styles.addParamError}>
          Error adding new parameter. Please contact{' '}
          <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>.
        </p>
      )}
      {showModal && (
        <CampaignCodeAdministrationAddNew
          active={showModal}
          toggleActive={setShowModal}
          onSubmit={async (nextNewParamDef: ParamDefs) => {
            const newParam = {
              ...nextNewParamDef,
              fieldAvailable: nextNewParamDef.fieldAvailable || false,
              helpText: nextNewParamDef.helpText || '',
            }

            await addNewParam({
              variables: newParam,
            })

            // Refresh the full generator
            // TODO: Improve cache
            // Every ParamDef could be its own cache object
            // Items should be read from it
            // Separate queries for getting validationChecks and paramDefs
            const { data: newGeneratorData } = await refetchGenerator()

            if (newGeneratorData) {
              setAccordionOpenState(
                newGeneratorData.campaignCodeGenerator.paramDefs.map(
                  (param, paramIndex, allParams) =>
                    paramIndex === allParams.length - 1,
                ),
              )
            }

            completeOnboardingSection()

            if (window.dataLayer && window.dataLayer.push) {
              window.dataLayer.push({
                event: 'add-generator-parameter',
                paramName: newParam.fieldName,
              })
            }

            trackAction(
              'add-generator-parameter',
              newParam.fieldName,
              JSON.stringify(newParam),
              '',
            )
          }}
        />
      )}
    </>
  )
}
